import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config";
import { SettingRow, SettingTextRow } from "./appConstants";

const WEEKDAYS = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
  { label: "Sun", value: 0 },
];

const STRIP_OPTIONS = [
  { code: "TOP", name: "Top" },
  { code: "BOTTOM", name: "Bottom" },
];

const SCREEN_OPTIONS = [
  { code: "SHUTTLE", name: "Shuttle screens" },
  { code: "STOP", name: "Stop screens" },
  { code: "BOTH", name: "Shuttle and stop" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": { height: "48px", borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A5DCF3" },
  backgroundColor: "#FFFFFF",
};

const EMPTY_FORM = {
  name: "",
  screenTarget: "BOTH",
  startsAt: "",
  endsAt: "",
  dailyStart: "",
  dailyEnd: "",
  weekdays: [],
  sortOrder: 0,
  active: true,
  file: null,
  tickerDescription: "",
  tickerLogo: null,
  removeTickerLogo: false,
};

const defaultSetup = (screenType) => ({
  screenType,
  durationSeconds: 5,
  intervalSeconds: 60,
  tickerPosition: "BOTTOM",
});

const defaultSetups = () => ({
  shuttle: defaultSetup("SHUTTLE"),
  stop: defaultSetup("STOP"),
});

function errorText(error) {
  return error?.response?.data?.error || error?.message || "Something went wrong";
}

function isoToDateInput(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date(value));
}

function dateInputToIso(value, end) {
  if (!value) return "";
  const time = end ? "23:59:59" : "00:00:00";
  return new Date(`${value}T${time}+05:30`).toISOString();
}

function appendSchedule(body, form) {
  body.append("startsAt", dateInputToIso(form.startsAt, false));
  body.append("endsAt", dateInputToIso(form.endsAt, true));
  body.append("dailyStart", form.dailyStart || "");
  body.append("dailyEnd", form.dailyEnd || "");
  body.append("weekdays", JSON.stringify(form.weekdays));
  body.append("sortOrder", String(form.sortOrder ?? 0));
  body.append("active", String(Boolean(form.active)));
  body.append("screenTarget", form.screenTarget);
  body.append("name", form.name.trim());
  body.append("tickerDescription", form.tickerDescription.trim());
  if (form.removeTickerLogo) body.append("removeTickerLogo", "true");
}

function splitClock(value) {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return { hour: "", minute: "", period: "AM" };
  }
  const [rawHour, rawMinute] = value.split(":").map(Number);
  const period = rawHour >= 12 ? "PM" : "AM";
  const hour12 = rawHour % 12 || 12;
  return {
    hour: String(hour12),
    minute: String(rawMinute).padStart(2, "0"),
    period,
  };
}

function joinClock(hour, minute, period) {
  if (!hour || minute === "") return "";
  const parsedMinute = Number(minute);
  if (!Number.isInteger(parsedMinute) || parsedMinute < 0 || parsedMinute > 59) return "";
  let parsedHour = Number(hour);
  if (!Number.isInteger(parsedHour) || parsedHour < 1 || parsedHour > 12) return "";
  if (period === "AM") {
    if (parsedHour === 12) parsedHour = 0;
  } else if (parsedHour !== 12) {
    parsedHour += 12;
  }
  return `${String(parsedHour).padStart(2, "0")}:${String(parsedMinute).padStart(2, "0")}`;
}

function ClockField({ label, value, onChange }) {
  const [parts, setParts] = useState(() => splitClock(value));

  useEffect(() => {
    setParts(splitClock(value));
  }, [value]);

  const update = (next) => {
    setParts(next);
    if (next.hour && next.minute !== "") {
      onChange(joinClock(next.hour, next.minute, next.period));
      return;
    }
    if (!next.hour && next.minute === "") onChange("");
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Typography className="form-label" sx={{ width: "auto", mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          select
          value={parts.hour}
          onChange={(event) => update({ ...parts, hour: event.target.value })}
          sx={{ ...fieldSx, minWidth: 72 }}
        >
          <MenuItem value="">Hr</MenuItem>
          {Array.from({ length: 12 }, (_, index) => String(index + 1)).map((hour) => (
            <MenuItem key={hour} value={hour}>{hour}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          value={parts.minute}
          onChange={(event) => update({ ...parts, minute: event.target.value })}
          sx={{ ...fieldSx, minWidth: 80 }}
        >
          <MenuItem value="">Min</MenuItem>
          {Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0")).map((minute) => (
            <MenuItem key={minute} value={minute}>{minute}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          value={parts.period}
          onChange={(event) => update({ ...parts, period: event.target.value })}
          sx={{ ...fieldSx, minWidth: 88 }}
        >
          <MenuItem value="AM">AM</MenuItem>
          <MenuItem value="PM">PM</MenuItem>
        </TextField>
      </Box>
    </Box>
  );
}

function logoButtonLabel(form, editing) {
  if (form.tickerLogo) return form.tickerLogo.name;
  if (form.removeTickerLogo) return "Logo will be removed";
  if (editing?.tickerLogoUrl) return "Replace logo";
  return "Choose logo";
}

function screenLabel(target) {
  if (target === "BOTH") return "Shuttle and stop";
  if (target === "SHUTTLE") return "Shuttle";
  return "Stop";
}

export default function Advertisements() {
  const [setups, setSetups] = useState(defaultSetups);
  const [savedSetups, setSavedSetups] = useState(defaultSetups);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/advertisements`);
      const nextSetups = {
        shuttle: response.data.setups?.shuttle || defaultSetup("SHUTTLE"),
        stop: response.data.setups?.stop || defaultSetup("STOP"),
      };
      setSetups(nextSetups);
      setSavedSetups(nextSetups);
      setAds(response.data.advertisements || []);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateSetup = (key, field, value) => {
    setSetups((current) => ({
      ...current,
      [key]: { ...current[key], [field]: value },
    }));
  };

  const handleSetupChange = (event) => {
    const { name, value } = event.target;
    const fields = {
      shuttleDuration: ["shuttle", "durationSeconds"],
      stopDuration: ["stop", "durationSeconds"],
      shuttleInterval: ["shuttle", "intervalSeconds"],
      stopInterval: ["stop", "intervalSeconds"],
      shuttleStrip: ["shuttle", "tickerPosition"],
      stopStrip: ["stop", "tickerPosition"],
    };
    const target = fields[name];
    if (!target) return;
    updateSetup(target[0], target[1], value);
  };

  const saveSetups = async () => {
    setSaving(true);
    try {
      await Promise.all(
        ["shuttle", "stop"].map((key) =>
          axios.put(`${config.baseURL}/advertisements/setup`, {
            screenType: setups[key].screenType,
            durationSeconds: Number(setups[key].durationSeconds),
            intervalSeconds: Number(setups[key].intervalSeconds),
            tickerPosition: setups[key].tickerPosition,
          }),
        ),
      );
      setSavedSetups(setups);
      toast.success("Advertisement settings saved");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (ad) => {
    setEditing(ad);
    setForm({
      name: ad.name,
      screenTarget: ad.screenTarget,
      startsAt: isoToDateInput(ad.startsAt),
      endsAt: isoToDateInput(ad.endsAt),
      dailyStart: ad.dailyStart || "",
      dailyEnd: ad.dailyEnd || "",
      weekdays: Array.isArray(ad.weekdays) ? ad.weekdays : [],
      sortOrder: ad.sortOrder ?? 0,
      active: ad.active !== false,
      file: null,
      tickerDescription: ad.tickerDescription || "",
      tickerLogo: null,
      removeTickerLogo: false,
    });
    setFormOpen(true);
  };

  const saveAd = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editing && !form.file) {
      toast.error("Choose a photo or video");
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      appendSchedule(body, form);
      if (form.file) body.append("media", form.file);
      if (form.tickerLogo) body.append("tickerLogo", form.tickerLogo);
      if (editing) {
        await axios.put(`${config.baseURL}/advertisements/${editing.id}`, body);
      } else {
        await axios.post(`${config.baseURL}/advertisements`, body);
      }
      setFormOpen(false);
      await load();
      toast.success(editing ? "Advertisement updated" : "Advertisement added");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const removeAd = async (ad) => {
    if (!window.confirm(`Delete ${ad.name}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${config.baseURL}/advertisements/${ad.id}`);
      await load();
      toast.success("Advertisement deleted");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleWeekday = (value) => {
    setForm((current) => {
      const selected = new Set(current.weekdays);
      if (selected.has(value)) selected.delete(value);
      else selected.add(value);
      return { ...current, weekdays: [...selected] };
    });
  };

  return (
    <Box>
      <Box className="settings-container">
        <SettingTextRow
          label="Shuttle ad length (seconds)"
          name="shuttleDuration"
          value={setups.shuttle.durationSeconds}
          onChange={handleSetupChange}
          disabled={saving || loading}
          type="number"
          min={1}
        />
        <SettingTextRow
          label="Stop ad length (seconds)"
          name="stopDuration"
          value={setups.stop.durationSeconds}
          onChange={handleSetupChange}
          disabled={saving || loading}
          type="number"
          min={1}
        />
        <SettingTextRow
          label="Shuttle time between ads (seconds)"
          name="shuttleInterval"
          value={setups.shuttle.intervalSeconds}
          onChange={handleSetupChange}
          disabled={saving || loading}
          type="number"
          min={5}
        />
        <SettingTextRow
          label="Stop time between ads (seconds)"
          name="stopInterval"
          value={setups.stop.intervalSeconds}
          onChange={handleSetupChange}
          disabled={saving || loading}
          type="number"
          min={5}
        />
        <SettingRow
          label="Shuttle details strip"
          name="shuttleStrip"
          value={setups.shuttle.tickerPosition}
          onChange={handleSetupChange}
          options={STRIP_OPTIONS}
          disabled={saving || loading}
        />
        <SettingRow
          label="Stop details strip"
          name="stopStrip"
          value={setups.stop.tickerPosition}
          onChange={handleSetupChange}
          options={STRIP_OPTIONS}
          disabled={saving || loading}
        />
      </Box>

      <Box className="button-container">
        <Button
          variant="outlined"
          onClick={() => setSetups(savedSetups)}
          disabled={saving || loading}
          sx={{
            height: "55px",
            minWidth: "120px",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "20px",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveSetups}
          disabled={saving || loading}
          sx={{
            width: 120,
            height: "55px",
            fontSize: "20px",
            color: "#1F242E",
            textTransform: "none",
            borderRadius: "8px",
            padding: "8px 24px",
            background: "#F9E719",
            border: "1px solid #414958",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#f1de16ff" },
            "&.Mui-disabled": {
              backgroundColor: "#E6E6E6",
              borderColor: "#C8CBD0",
              color: "#7A7A7A",
            },
          }}
        >
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </Box>

      <Box className="settings-container" sx={{ mt: 4, gridTemplateColumns: "1fr" }}>
        <Box className="setting-row">
          <Typography className="form-label">Advertisements</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={openCreate}
              disabled={loading}
              sx={{
                height: "48px",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                color: "#414958",
                borderColor: "#C8CBD0",
              }}
            >
              Add advertisement
            </Button>
          </Box>
        </Box>

        {!loading && ads.length === 0 ? (
          <Typography className="form-label" sx={{ width: "auto" }}>
            No advertisements yet.
          </Typography>
        ) : null}

        {ads.map((ad) => (
          <Box className="setting-row" key={ad.id}>
            <Typography className="form-label">{ad.name}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              {ad.mediaType === "VIDEO" ? (
                <video
                  src={ad.url}
                  muted
                  style={{ width: 96, height: 48, objectFit: "cover", borderRadius: 8, background: "#F4F5F7" }}
                />
              ) : (
                <img
                  src={ad.url}
                  alt=""
                  style={{ width: 96, height: 48, objectFit: "cover", borderRadius: 8, background: "#F4F5F7" }}
                />
              )}
              {ad.tickerLogoUrl ? (
                <img
                  src={ad.tickerLogoUrl}
                  alt=""
                  style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 8, background: "#FFFFFF" }}
                />
              ) : null}
              <Typography sx={{ color: "#414958", fontSize: 16, flex: 1, minWidth: 0 }}>
                {screenLabel(ad.screenTarget)}
                {" · "}
                {ad.mediaType === "VIDEO" ? "Video" : "Photo"}
                {" · "}
                {ad.active ? "Active" : "Paused"}
                {ad.tickerDescription ? ` · ${ad.tickerDescription}` : ""}
              </Typography>
              <Button onClick={() => openEdit(ad)} sx={{ textTransform: "none", color: "#414958", fontWeight: 600 }}>
                Edit
              </Button>
              <Button onClick={() => removeAd(ad)} sx={{ textTransform: "none", color: "#414958", fontWeight: 600 }}>
                Delete
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: "#414958", fontWeight: 600 }}>
          {editing ? "Edit advertisement" : "Add advertisement"}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: "20px !important",
          }}
        >
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            fullWidth
            sx={{
              ...fieldSx,
              mt: 1,
              "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                backgroundColor: "#fff",
                paddingLeft: "4px",
                paddingRight: "4px",
              },
            }}
          />
          <Button
            variant="outlined"
            component="label"
            sx={{
              height: "48px",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              color: "#414958",
              borderColor: "#C8CBD0",
            }}
          >
            {form.file ? form.file.name : editing ? "Replace photo or video" : "Choose photo or video"}
            <input
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4"
              onChange={(event) =>
                setForm((current) => ({ ...current, file: event.target.files?.[0] || null }))
              }
            />
          </Button>
          <TextField
            label="Moving description"
            value={form.tickerDescription}
            onChange={(event) =>
              setForm((current) => ({ ...current, tickerDescription: event.target.value }))
            }
            inputProps={{ maxLength: 240 }}
            helperText="Shown on the bottom bar while the live screen is up. The words move from left to right."
            fullWidth
            sx={{
              ...fieldSx,
              "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                backgroundColor: "#fff",
                paddingLeft: "4px",
                paddingRight: "4px",
              },
            }}
          />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="outlined"
              component="label"
              sx={{
                height: "48px",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                color: "#414958",
                borderColor: "#C8CBD0",
                flex: 1,
              }}
            >
              {logoButtonLabel(form, editing)}
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    tickerLogo: event.target.files?.[0] || null,
                    removeTickerLogo: false,
                  }))
                }
              />
            </Button>
            {(editing?.tickerLogoUrl || form.tickerLogo) && !form.removeTickerLogo ? (
              <Button
                onClick={() =>
                  setForm((current) => ({ ...current, tickerLogo: null, removeTickerLogo: true }))
                }
                sx={{ textTransform: "none", color: "#414958", fontWeight: 600, height: "48px" }}
              >
                Remove logo
              </Button>
            ) : null}
          </Box>
          <TextField
            select
            label="Show on"
            value={form.screenTarget}
            onChange={(event) => setForm((current) => ({ ...current, screenTarget: event.target.value }))}
            sx={fieldSx}
          >
            {SCREEN_OPTIONS.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start date"
              type="date"
              value={form.startsAt}
              onChange={(event) => setForm((current) => ({ ...current, startsAt: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="End date"
              type="date"
              value={form.endsAt}
              onChange={(event) => setForm((current) => ({ ...current, endsAt: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={fieldSx}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <ClockField
              label="Daily from"
              value={form.dailyStart}
              onChange={(value) => setForm((current) => ({ ...current, dailyStart: value }))}
            />
            <ClockField
              label="Daily until"
              value={form.dailyEnd}
              onChange={(value) => setForm((current) => ({ ...current, dailyEnd: value }))}
            />
          </Box>
          <Box>
            <Typography className="form-label" sx={{ width: "auto", mb: 1 }}>
              Weekdays (leave empty for every day)
            </Typography>
            {WEEKDAYS.map((day) => (
              <FormControlLabel
                key={day.label}
                control={
                  <Checkbox
                    checked={form.weekdays.includes(day.value)}
                    onChange={() => toggleWeekday(day.value)}
                  />
                }
                label={day.label}
              />
            ))}
          </Box>
          <TextField
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(event) => setForm((current) => ({ ...current, sortOrder: event.target.value }))}
            inputProps={{ min: 0, max: 9999 }}
            sx={fieldSx}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setFormOpen(false)}
            variant="outlined"
            sx={{
              height: "48px",
              minWidth: "120px",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={saveAd}
            sx={{
              height: "48px",
              minWidth: "120px",
              fontSize: "16px",
              color: "#1F242E",
              textTransform: "none",
              borderRadius: "8px",
              background: "#F9E719",
              border: "1px solid #414958",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#f1de16ff" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
